import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const thStyleCompact = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 800,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
}
const tdStyle = { padding: '8px 10px', verticalAlign: 'top' }
import { VENTANAS_HORARIAS, labelVentana } from '../../lib/ventanasHorarias'
import { WA_NUMERO } from '../../lib/constantes'
import {
  buscarPersonas, buscarPorDocumento, crearPersona,
  crearSuscripcion, actualizarSuscripcion, actualizarEstadoSuscripcion,
  actualizarPersona,
  getKits, getPorterosActivos, getBarrios,
} from '../../lib/clientesApi'
import {
  getPagosCliente, registrarPago, crearPagoManual, cancelarPago,
  getCuentaCobro, textoWhatsAppCobro,
  ESTADO_PAGO_LABEL, ESTADO_PAGO_COLOR,
} from '../../lib/cobrosApi'
import { getSuscripciones } from '../../lib/entregasApi'
import { validarDocumento, validarEmail, validarTelUY, formatearTelUY, formatearCI, validarCI } from '../../lib/documentos'
import { geocodificar, validarBarrioConDireccion } from '../../lib/geo'
import MapPin from '../../components/MapPin'

const VIVIENDA_LABEL = { casa: '🏠 Casa', edificio: '🏢 Edificio', oficina: '🏢 Oficina' }
const SEMANA_LABEL = { 1: '1ra', 2: '2da', 3: '3ra', 4: '4ta' }
const semanaLabel = (n) => SEMANA_LABEL[n] || `${n}ra`
const ESTADO_COLOR = {
  activa: { bg: '#D1FAE5', text: '#065F46' },
  pausada: { bg: '#FEF3C7', text: '#92400E' },
  cancelada: { bg: '#FEE2E2', text: '#991B1B' },
}

// ── Helpers de presentación (rebuild lista 2026-09-02) ──
const AV_COLORS = ['#00A8B5', '#1A3461', '#0369A1', '#5BBDD8', '#0E7490', '#334155']
function iniciales(nombre) {
  const p = String(nombre || '?').trim().split(/\s+/)
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?'
}
function avColor(nombre) {
  const s = String(nombre || '')
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AV_COLORS[h % AV_COLORS.length]
}
const fmtMoney = (n) => '$ ' + Math.round(Number(n || 0)).toLocaleString('es-UY')
const Avatar = ({ nombre, color }) => (
  <div style={{ width: 34, height: 34, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12.5, color: '#fff', background: color || avColor(nombre) }}>{iniciales(nombre)}</div>
)
const Badge = ({ children, bg, color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: bg, color }}>{children}</span>
)

export default function Clientes() {
  const [suscripciones, setSuscripciones] = useState([])
  const [oneShot, setOneShot] = useState([])
  const [b2b, setB2b] = useState([])
  const [kitPrecio, setKitPrecio] = useState({})
  const [loading, setLoading] = useState(true)
  const [seg, setSeg] = useState('todos')          // todos | susc | oneshot | b2b
  const [estadoF, setEstadoF] = useState('todas')  // todas | activa | pausada | cancelada (solo suscriptores)
  const [soloConvertir, setSoloConvertir] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [showAlta, setShowAlta] = useState(false)
  const [showChooser, setShowChooser] = useState(false)
  const [showContacto, setShowContacto] = useState(false)
  const [ocultarTests, setOcultarTests] = useState(true)
  const [editando, setEditando] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const clienteFiltradoId = searchParams.get('cliente') || searchParams.get('q')
  const [expandidoId, setExpandidoId] = useState(null)
  const [actividadPorCliente, setActividadPorCliente] = useState({})

  useEffect(() => { load() }, [])
  useEffect(() => { if (searchParams.get('alta') === '1') setShowAlta(true) }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    const [susc, kits] = await Promise.all([getSuscripciones(), getKits().catch(() => [])])
    setSuscripciones(susc)
    const kp = {}
    for (const k of (kits || [])) { if (k.codigo != null) kp[k.codigo] = Number(k.precio_susc || 0); if (k.nombre) kp[k.nombre] = Number(k.precio_susc || 0) }
    setKitPrecio(kp)

    const titularIds = [...new Set(susc.map(s => s.titular?.id).filter(Boolean))]
    const [pedOne, entHechas, emps, pedB2B] = await Promise.all([
      supabase.from('pedidos')
        .select('comprador_profile_id, comprador_nombre, comprador_telefono, entrega_direccion, monto_total, created_at, tipo')
        .is('empresa_id', null).in('tipo', ['compra_unica', 'productos_sueltos'])
        .not('pagado_at', 'is', null).eq('es_prueba', false),
      titularIds.length
        ? supabase.from('entregas').select('cliente_id, estado').in('cliente_id', susc.map(s => s.id)).eq('estado', 'entregada')
        : Promise.resolve({ data: [] }),
      supabase.from('empresas').select('id, razon_social, nombre_fantasia').eq('estado_pipeline', 'activo').is('archivada_at', null),
      supabase.from('pedidos').select('empresa_id, monto_total, estado').not('empresa_id', 'is', null).eq('es_prueba', false),
    ])
    const pedidosOne = pedOne.data || []

    // Actividad por titular (para las filas de suscriptor)
    const act = {}
    for (const p of pedidosOne) {
      const k = p.comprador_profile_id
      if (!act[k]) act[k] = { oneShots: 0, montoOneShot: 0, entregasHechas: 0 }
      act[k].oneShots++; act[k].montoOneShot += Number(p.monto_total || 0)
    }
    const entBySusc = {}
    for (const e of (entHechas.data || [])) entBySusc[e.cliente_id] = (entBySusc[e.cliente_id] || 0) + 1
    for (const s of susc) { const t = s.titular?.id; if (!t) continue; if (!act[t]) act[t] = { oneShots: 0, montoOneShot: 0, entregasHechas: 0 }; act[t].entregasHechas += entBySusc[s.id] || 0 }
    setActividadPorCliente(act)

    // One-shot: agrupar por comprador, excluir a los que ya son suscriptores
    const byBuyer = {}
    for (const p of pedidosOne) {
      const k = p.comprador_profile_id || ('anon_' + (p.comprador_nombre || ''))
      if (!byBuyer[k]) byBuyer[k] = { id: p.comprador_profile_id, nombre: p.comprador_nombre || 'Comprador', telefono: p.comprador_telefono || '', direccion: p.entrega_direccion || '', compras: 0, gastado: 0, ultima: null }
      const b = byBuyer[k]
      b.compras++; b.gastado += Number(p.monto_total || 0)
      if (p.entrega_direccion) b.direccion = p.entrega_direccion
      const t = new Date(p.created_at).getTime(); if (!b.ultima || t > b.ultima) b.ultima = t
    }
    setOneShot(Object.values(byBuyer).filter(b => !(b.id && titularIds.includes(b.id))).sort((a, b) => b.gastado - a.gastado))

    // B2B
    const cnt = {}
    for (const p of (pedB2B.data || [])) {
      const k = p.empresa_id
      if (!cnt[k]) cnt[k] = { pedidos: 0, facturado: 0 }
      cnt[k].pedidos++
      if (['entregado', 'en_ruta', 'pagado'].includes(p.estado)) cnt[k].facturado += Number(p.monto_total || 0)
    }
    setB2b((emps.data || []).map(e => ({ id: e.id, nombre: e.nombre_fantasia || e.razon_social, razon: e.razon_social, pedidos: cnt[e.id]?.pedidos || 0, facturado: cnt[e.id]?.facturado || 0 })).sort((a, b) => b.facturado - a.facturado))

    setLoading(false)
  }

  function toggleExpandir(id) { setExpandidoId(prev => prev === id ? null : id) }
  function clearClienteFiltro() { const n = new URLSearchParams(searchParams); n.delete('cliente'); n.delete('q'); setSearchParams(n) }
  const mrrSusc = (s) => Number(s.kit?.precio_susc) || kitPrecio[s.kit?.codigo] || kitPrecio[s.kit?.nombre] || 0

  // Base suscriptores (oculta tests salvo toggle)
  const baseSusc = useMemo(() => ocultarTests ? suscripciones.filter(s => !s.es_prueba) : suscripciones, [suscripciones, ocultarTests])
  const testsOcultos = useMemo(() => suscripciones.filter(s => s.es_prueba).length, [suscripciones])
  const q = busqueda.trim().toLowerCase()

  // Deep-link ?cliente=UUID → mostrar solo ese
  const soloUno = clienteFiltradoId ? suscripciones.filter(s => s.id === clienteFiltradoId) : null

  const mrrTotal = useMemo(() => baseSusc.filter(s => s.estado === 'activa').reduce((a, s) => a + mrrSusc(s), 0), [baseSusc, kitPrecio])
  const kpis = useMemo(() => ({
    activas: baseSusc.filter(s => s.estado === 'activa').length,
    canceladas: baseSusc.filter(s => s.estado === 'cancelada').length,
    oneshot: oneShot.length,
    convertir: oneShot.filter(o => o.compras > 1).length,
    b2b: b2b.length,
  }), [baseSusc, oneShot, b2b])

  const matchSuscQ = (s) => !q || [s.titular?.nombre, s.beneficiario?.nombre, s.barrio_obj?.nombre, s.barrio, s.direccion, s.alias].some(x => x?.toLowerCase().includes(q))
  const suscFilt = useMemo(() => baseSusc.filter(s => (estadoF === 'todas' || s.estado === estadoF) && matchSuscQ(s)), [baseSusc, estadoF, q])
  const oneFilt = useMemo(() => oneShot.filter(o => (!soloConvertir || o.compras > 1) && (!q || o.nombre.toLowerCase().includes(q) || o.direccion.toLowerCase().includes(q))), [oneShot, soloConvertir, q])
  const b2bFilt = useMemo(() => b2b.filter(e => !q || e.nombre.toLowerCase().includes(q) || e.razon?.toLowerCase().includes(q)), [b2b, q])

  const showSusc = seg === 'todos' || seg === 'susc'
  const showOne = seg === 'todos' || seg === 'oneshot'
  const showB2b = seg === 'todos' || seg === 'b2b'
  const hayFilas = (showSusc && suscFilt.length) || (showOne && oneFilt.length) || (showB2b && b2bFilt.length)

  const kpi = (key, val, label, sub, color, onClick, on) => (
    <div className="card" onClick={onClick} style={{ cursor: 'pointer', borderTop: `3px solid ${color}`, ...(on ? { boxShadow: `0 0 0 1px ${color}` } : {}) }}>
      <h3>{label}</h3><div className="stat" style={{ color }}>{val}</div><div className="desc">{sub}</div>
    </div>
  )
  const Seg = ({ k, children }) => (
    <button className="btn ghost" style={{ padding: '5px 12px', fontSize: 12.5, borderRadius: 999, ...(seg === k ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
      onClick={() => { setSeg(k); if (k !== 'susc') setEstadoF('todas'); if (k !== 'oneshot') setSoloConvertir(false) }}>{children}</button>
  )
  const Divider = ({ children }) => (
    <tr><td colSpan={8} style={{ padding: '7px 12px 5px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: '#94A3B8', fontWeight: 800, background: '#F8FBFD' }}>{children}</td></tr>
  )

  return (
    <AdminLayout>
      <div className="page">
        <h1>Clientes</h1>
        <p className="lead">Suscriptores, one-shot y empresas B2B en una sola lista.</p>

        {clienteFiltradoId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14, background: '#E0F7F9', border: '1px solid #00A8B5', borderLeft: '4px solid #00A8B5', borderRadius: 8, fontSize: 13 }}>
            <span style={{ color: '#0E7490', fontWeight: 700 }}>🔍 Filtrado por cliente: <span style={{ color: '#1A3461' }}>{soloUno?.[0]?.titular?.nombre || 'cliente'}</span></span>
            <button onClick={clearClienteFiltro} style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #00A8B5', color: '#0E7490', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>× Quitar filtro</button>
          </div>
        )}

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          {kpi('susc', kpis.activas, 'Suscriptores', mrrTotal > 0 ? `MRR ${fmtMoney(mrrTotal)}` : 'activas', '#00A8B5', () => { setSeg('susc'); setEstadoF('activa') }, seg === 'susc' && estadoF === 'activa')}
          {kpi('oneshot', kpis.oneshot, 'One-shot', 'compra única', '#0369A1', () => { setSeg('oneshot'); setSoloConvertir(false) }, seg === 'oneshot' && !soloConvertir)}
          {kpi('conv', kpis.convertir, 'A convertir', 'compraron 2+ veces', '#B45309', () => { setSeg('oneshot'); setSoloConvertir(true) }, soloConvertir)}
          {kpi('canc', kpis.canceladas, 'Canceladas', 'suscripción dada de baja', '#B91C1C', () => { setSeg('susc'); setEstadoF('cancelada') }, seg === 'susc' && estadoF === 'cancelada')}
          {kpi('b2b', kpis.b2b, 'Empresas B2B', 'pipeline activo', '#1A3461', () => setSeg('b2b'), seg === 'b2b')}
        </div>

        <div className="btn-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={() => setShowChooser(true)}>+ Nuevo cliente</button>
          <Link to="/admin/clientes/fuera-zona" className="btn ghost" style={{ borderColor: '#FCA5A5', color: '#B91C1C' }} title="Alta excepción fuera de cobertura">🚫 Fuera de zona</Link>
          <input type="search" placeholder="Buscar por nombre, dirección, barrio…" value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--line)', fontSize: 14 }} />
          <button className="btn ghost" onClick={load} title="Actualizar">↺</button>
        </div>

        <div className="btn-row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
          <Seg k="todos">Todos</Seg>
          <Seg k="susc">Suscriptores ({baseSusc.length})</Seg>
          <Seg k="oneshot">One-shot ({oneShot.length})</Seg>
          <Seg k="b2b">B2B ({b2b.length})</Seg>
          {seg === 'susc' && ['todas', 'activa', 'pausada', 'cancelada'].map(f => (
            <button key={f} className="btn ghost" style={{ padding: '4px 10px', fontSize: 11.5, ...(estadoF === f ? { background: '#0EA5A5', color: '#fff', borderColor: '#0EA5A5' } : {}) }} onClick={() => setEstadoF(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
          {testsOcultos > 0 && (
            <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 11.5, marginLeft: 'auto', ...(ocultarTests ? { background: '#0EA5A5', color: '#fff', borderColor: '#0EA5A5' } : {}) }} onClick={() => setOcultarTests(v => !v)} title="Ocultar/mostrar cuentas de prueba">🧪 {ocultarTests ? `Tests ocultos (${testsOcultos})` : `Mostrando tests`}</button>
          )}
        </div>

        {loading ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>
        ) : soloUno ? (
          <TablaClientes>{soloUno.map(s => renderSusc(s))}</TablaClientes>
        ) : !hayFilas ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>No hay clientes con esos filtros.</div>
        ) : (
          <div style={{ marginTop: 14, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={thStyleCompact}>Cliente</th>
                  <th style={thStyleCompact}>Dirección · Barrio</th>
                  <th style={thStyleCompact}>Kit · Día</th>
                  <th style={thStyleCompact}>Origen</th>
                  <th style={thStyleCompact}>Tipo · Estado</th>
                  <th style={thStyleCompact}>Actividad</th>
                  <th style={{ ...thStyleCompact, textAlign: 'right' }}>MRR / Gastado</th>
                  <th style={{ ...thStyleCompact, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {showSusc && suscFilt.map(s => renderSusc(s))}
                {showOne && oneFilt.length > 0 && seg === 'todos' && <Divider>◆ One-shot — compraron sin suscribirse</Divider>}
                {showOne && oneFilt.map(o => renderOne(o))}
                {showB2b && b2bFilt.length > 0 && seg === 'todos' && <Divider>🏢 Empresas B2B</Divider>}
                {showB2b && b2bFilt.map(e => renderB2b(e))}
              </tbody>
            </table>
            <div style={{ padding: '8px 14px', fontSize: 11, color: '#64748B', background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
              {(showSusc ? suscFilt.length : 0) + (showOne ? oneFilt.length : 0) + (showB2b ? b2bFilt.length : 0)} cliente(s) · click en un suscriptor para ver detalle
            </div>
          </div>
        )}

        {showChooser && <ElegirTipoCliente
          onClose={() => setShowChooser(false)}
          onSuscripcion={() => { setShowChooser(false); setShowAlta(true) }}
          onContacto={() => { setShowChooser(false); setShowContacto(true) }} />}
        {showContacto && <ModalContacto onClose={() => setShowContacto(false)} onCreated={() => { setShowContacto(false); load() }} />}
        {showAlta && <ModalAlta onClose={() => setShowAlta(false)} onCreated={() => { setShowAlta(false); load() }} />}
        {editando && <ModalEditar suscripcion={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); load() }} />}
      </div>
    </AdminLayout>
  )

  // ── renderers ──
  function renderSusc(s) {
    const recibe = s.beneficiario || s.titular
    const esRegalo = s.beneficiario && s.beneficiario.id !== s.titular?.id
    const cfg = ESTADO_COLOR[s.estado] || ESTADO_COLOR.activa
    const act = actividadPorCliente[s.titular?.id] || { oneShots: 0, montoOneShot: 0, entregasHechas: 0 }
    const expandido = expandidoId === s.id
    const mrr = mrrSusc(s)
    return (
      <React.Fragment key={'s_' + s.id}>
        <tr onClick={() => toggleExpandir(s.id)} style={{ borderBottom: expandido ? '0' : '1px solid #F1F5F9', cursor: 'pointer', background: expandido ? '#F8FAFC' : '#fff' }}>
          <td style={tdStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#94A3B8', fontSize: 10 }}>{expandido ? '▼' : '▶'}</span>
              <Avatar nombre={recibe?.nombre} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 12.5, color: '#1A3461' }}>{recibe?.nombre || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748B' }}>{recibe?.phone ? formatearTelUY(recibe.phone) : (esRegalo ? '🎁 paga: ' + (s.titular?.nombre || '') : 'titular')}</div>
              </div>
            </div>
          </td>
          <td style={tdStyle}>
            {s.alias && <div style={{ fontSize: 10.5, fontWeight: 800, color: '#1A3461', textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.alias}</div>}
            {s.fuera_zona && <div style={{ display: 'inline-block', fontSize: 9.5, fontWeight: 700, padding: '1px 6px', background: '#FEF3C7', color: '#92400E', borderRadius: 999, marginBottom: 2 }}>🚫 Fuera zona</div>}
            <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{s.barrio_obj?.nombre || s.barrio || '—'}</div>
            <div style={{ fontSize: 11, color: '#64748B' }}>{s.direccion}{s.apto ? ` · ${s.apto}` : ''}</div>
          </td>
          <td style={tdStyle}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1A3461' }}>{s.kit?.nombre || '—'}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', textTransform: 'capitalize' }}>
              {s.semana_mes && <strong>{semanaLabel(s.semana_mes)} sem · </strong>}{s.dia_entrega || '—'} · {(VENTANAS_HORARIAS.find(v => v.key === s.ventana_horaria)?.hi || '')}-{(VENTANAS_HORARIAS.find(v => v.key === s.ventana_horaria)?.hf || '')}hs
            </div>
          </td>
          <td style={{ ...tdStyle, fontSize: 11 }}>
            {s.portero?.lugar ? <><div style={{ fontWeight: 600, fontSize: 11.5 }}>🏛 {s.portero.lugar}</div><div style={{ color: '#64748B', fontSize: 10.5 }}>{s.portero.profile?.nombre}</div></> : <span style={{ color: '#94A3B8' }}>directo</span>}
          </td>
          <td style={tdStyle}>
            <Badge bg="#E1F5EE" color="#065F46">Suscriptor</Badge>
            <div style={{ marginTop: 3 }}><Badge bg={cfg.bg} color={cfg.text}><span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.text, display: 'inline-block' }} />{s.estado}</Badge></div>
            {s.pausa_hasta && <div style={{ fontSize: 9.5, color: '#64748B', marginTop: 2 }}>hasta {s.pausa_hasta}</div>}
          </td>
          <td style={tdStyle}>
            <div style={{ fontSize: 10.5, color: '#475569' }}>{act.entregasHechas > 0 ? `${act.entregasHechas} entregas` : 'sin entregas'}</div>
            {act.oneShots > 0 && <div style={{ fontSize: 10, color: '#78350F' }}>🛍 {act.oneShots} one-shot · {fmtMoney(act.montoOneShot)}</div>}
          </td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>
            <span style={{ fontFamily: 'Inter', fontWeight: 800, color: s.estado === 'activa' ? '#1A3461' : '#94A3B8' }}>{s.estado === 'activa' && mrr > 0 ? fmtMoney(mrr) : '—'}</span>
            {s.estado === 'activa' && mrr > 0 && <div style={{ fontSize: 9.5, color: '#94A3B8' }}>/ mes</div>}
          </td>
          <td style={{ ...tdStyle, textAlign: 'right' }} onClick={ev => ev.stopPropagation()}>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditando(s)} title="Editar" style={{ background: 'transparent', border: '1px solid #E2E8F0', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>✏️</button>
              <BotonesAccion suscripcion={s} onChange={load} />
            </div>
          </td>
        </tr>
        {expandido && <tr><td colSpan={8} style={{ padding: 0, background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}><DetalleAccordion suscripcion={s} actividad={act} /></td></tr>}
      </React.Fragment>
    )
  }

  function renderOne(o) {
    const wa = o.telefono ? `https://wa.me/${String(o.telefono).replace(/[^0-9]/g, '').replace(/^0/, '').replace(/^(?!598)/, '598')}` : null
    const recurrente = o.compras > 1
    return (
      <tr key={'o_' + (o.id || o.nombre)} style={{ borderBottom: '1px solid #F1F5F9' }}>
        <td style={tdStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 10 }} />
            <Avatar nombre={o.nombre} color="#0369A1" />
            <div><div style={{ fontWeight: 700, fontSize: 12.5, color: '#1A3461' }}>{o.nombre}</div><div style={{ fontSize: 11, color: '#64748B' }}>{o.telefono ? formatearTelUY(o.telefono) : 'comprador'}</div></div>
          </div>
        </td>
        <td style={tdStyle}><div style={{ fontSize: 11.5, color: '#334155' }}>{o.direccion || '—'}</div></td>
        <td style={{ ...tdStyle, color: '#94A3B8' }}>—</td>
        <td style={{ ...tdStyle, fontSize: 11 }}><span style={{ color: '#94A3B8' }}>tienda web</span></td>
        <td style={tdStyle}>
          <Badge bg="#E0F2FE" color="#0369A1">◆ One-shot</Badge>
          {recurrente && <div style={{ marginTop: 3 }}><Badge bg="#FEF3C7" color="#B45309">★ A convertir</Badge></div>}
        </td>
        <td style={{ ...tdStyle, fontSize: 10.5, color: '#475569' }}>{o.compras} compra{o.compras !== 1 ? 's' : ''}{o.ultima ? ` · última ${new Date(o.ultima).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' })}` : ''}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}><span style={{ fontFamily: 'Inter', fontWeight: 800, color: '#1A3461' }}>{fmtMoney(o.gastado)}</span><div style={{ fontSize: 9.5, color: '#94A3B8' }}>gastado</div></td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            {o.id && <Link to={`/admin/clientes/comprador/${o.id}`} title="Ver ficha del comprador" style={{ border: '1px solid #E2E8F0', borderRadius: 6, padding: '3px 8px', fontSize: 12, textDecoration: 'none', color: '#0369A1' }}>↗</Link>}
            <button onClick={() => setShowAlta(true)} title="Convertir a suscripción" style={{ background: '#E1F5EE', border: '1px solid #A7E3D4', color: '#065F46', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>🔁</button>
            {wa && <a href={wa} target="_blank" rel="noopener noreferrer" title="WhatsApp" style={{ border: '1px solid #E2E8F0', borderRadius: 6, padding: '3px 8px', fontSize: 12, textDecoration: 'none' }}>💬</a>}
          </div>
        </td>
      </tr>
    )
  }

  function renderB2b(e) {
    return (
      <tr key={'b_' + e.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
        <td style={tdStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 10 }} />
            <Avatar nombre={e.nombre} color="#1A3461" />
            <div><div style={{ fontWeight: 700, fontSize: 12.5, color: '#1A3461' }}>{e.nombre}</div><div style={{ fontSize: 11, color: '#64748B' }}>{e.razon}</div></div>
          </div>
        </td>
        <td style={{ ...tdStyle, color: '#94A3B8' }}>empresa</td>
        <td style={{ ...tdStyle, color: '#94A3B8' }}>—</td>
        <td style={{ ...tdStyle, fontSize: 11 }}><span style={{ color: '#94A3B8' }}>B2B</span></td>
        <td style={tdStyle}><Badge bg="#DBEAFE" color="#1E3A8A">🏢 B2B</Badge></td>
        <td style={{ ...tdStyle, fontSize: 10.5, color: '#475569' }}>{e.pedidos} pedido{e.pedidos !== 1 ? 's' : ''}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}><span style={{ fontFamily: 'Inter', fontWeight: 800, color: '#1A3461' }}>{fmtMoney(e.facturado)}</span><div style={{ fontSize: 9.5, color: '#94A3B8' }}>facturado</div></td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>
          <Link to={`/admin/empresas/${e.id}`} title="Ver empresa" style={{ border: '1px solid #E2E8F0', borderRadius: 6, padding: '3px 8px', fontSize: 12, textDecoration: 'none' }}>↗</Link>
        </td>
      </tr>
    )
  }
}

function TablaClientes({ children }) {
  return (
    <div style={{ marginTop: 14, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
          <th style={thStyleCompact}>Cliente</th><th style={thStyleCompact}>Dirección · Barrio</th><th style={thStyleCompact}>Kit · Día</th><th style={thStyleCompact}>Origen</th><th style={thStyleCompact}>Tipo · Estado</th><th style={thStyleCompact}>Actividad</th><th style={{ ...thStyleCompact, textAlign: 'right' }}>MRR / Gastado</th><th style={thStyleCompact}></th>
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}


function BotonesAccion({ suscripcion: s, onChange }) {
  async function pausar() {
    const fecha = prompt('¿Hasta qué fecha? (YYYY-MM-DD, vacío = sin límite)')
    if (fecha === null) return
    try {
      await actualizarEstadoSuscripcion(s.id, 'pausada', fecha || null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  async function reactivar() {
    try {
      await actualizarEstadoSuscripcion(s.id, 'activa', null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  async function cancelar() {
    if (!confirm('¿Cancelar esta suscripción? Se puede reactivar después.')) return
    try {
      await actualizarEstadoSuscripcion(s.id, 'cancelada', null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {s.estado === 'activa' && <button className="btn ghost" style={btnSm} onClick={pausar}>⏸</button>}
      {s.estado === 'pausada' && <button className="btn ghost" style={btnSm} onClick={reactivar}>▶</button>}
      {s.estado !== 'cancelada' && <button className="btn ghost" style={{ ...btnSm, color: '#991B1B' }} onClick={cancelar}>✕</button>}
    </div>
  )
}
const btnSm = { padding: '4px 8px', fontSize: 11 }

/* ───────── Modal Alta (single page con checkbox beneficiario) ───────── */

function ModalAlta({ onClose, onCreated }) {
  const [titular, setTitular] = useState(null)
  const [beneficiario, setBeneficiario] = useState(null)
  const [mismoBenef, setMismoBenef] = useState(true)  // checkbox: el beneficiario es el mismo
  const [form, setForm] = useState({
    alias: '', barrioId: '', direccion: '', apto: '', tipoVivienda: '',
    kitCodigo: '', diaEntrega: '', ventanaHoraria: 'manana', semanaMes: '',
    diaCobroMes: String(new Date().getDate()),  // default: día de hoy
    modeloCobro: 'prepaid_mensiversario',
    porteroReferidoId: '', notas: '',
  })
  const [coords, setCoords] = useState(null)  // { lat, lng } para el pin del mapa
  const [coordsExactas, setCoordsExactas] = useState(false)  // checkbox "el pin está bien"
  const [geoErr, setGeoErr] = useState(null)
  const [barrioMismatch, setBarrioMismatch] = useState(null)  // {ok, sugerenciaActivo, sinCobertura, mensaje} si OSM detectó otro barrio
  const [geocodando, setGeocodando] = useState(false)
  const [barrios, setBarrios] = useState([])
  const [kits, setKits] = useState([])
  const [porteros, setPorteros] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Barrio seleccionado (objeto completo) y días disponibles
  const barrioSeleccionado = useMemo(() => barrios.find(b => b.id === form.barrioId), [barrios, form.barrioId])
  const diasDisponibles = barrioSeleccionado?.dias || []

  /** Busca la dirección en OSM y pone el pin. Si falla, cae al centro del barrio.
   *  Además: valida que el barrio detectado por OSM coincida con el elegido. */
  async function geocodearDireccion() {
    setGeoErr(null)
    setCoordsExactas(false)
    setBarrioMismatch(null)
    const barrioNombre = barrioSeleccionado?.nombre
    if (!barrioNombre) return
    setGeocodando(true)
    try {
      // 1. Intentar dirección exacta
      if (form.direccion.trim()) {
        const r = await geocodificar(`${form.direccion}, ${barrioNombre}`)
        if (r) {
          setCoords({ lat: r.lat, lng: r.lng })
          setCoordsExactas(true)
          // Validar que el barrio detectado por OSM coincida con el elegido
          const v = validarBarrioConDireccion(r.barrioDetectado, barrioNombre, barrios.filter(b => b.activo))
          if (!v.ok) setBarrioMismatch(v)
          return
        }
      }
      // 2. Fallback: centro del barrio (siempre tenemos algo donde mover el pin)
      const rb = await geocodificar(barrioNombre)
      if (rb) {
        setCoords({ lat: rb.lat, lng: rb.lng })
        setGeoErr('No pude encontrar la dirección exacta. El pin está en el centro del barrio — movelo al lugar correcto.')
      } else {
        // Última opción: centro de Montevideo
        setCoords({ lat: -34.9011, lng: -56.1645 })
        setGeoErr('Centro de Montevideo. Movelo al lugar correcto.')
      }
    } finally { setGeocodando(false) }
  }

  useEffect(() => {
    Promise.all([getKits(), getPorterosActivos(), getBarrios()]).then(([k, p, b]) => {
      setKits(k); setPorteros(p); setBarrios(b)
    })
  }, [])

  async function handleSubmit() {
    setErr(null)
    // BLOQUEO DURO si OSM detectó zona fuera de cobertura.
    // El admin no puede guardar — solo cerrar y avisar al equipo por WhatsApp.
    if (barrioMismatch?.sinCobertura) {
      setErr('No se puede guardar: la dirección está fuera de la zona de cobertura actual de Limpack. Avisá al equipo desde el botón de WhatsApp para evaluar si se suma el barrio.')
      return
    }
    // Para mismatch de barrio cubierto (no fuera de cobertura): confirm suave.
    if (barrioMismatch && !barrioMismatch.ok) {
      const sug = barrioMismatch.sugerenciaActivo
      const elegido = barrioSeleccionado?.nombre
      const msg = sug
        ? `OpenStreetMap detectó esta dirección en ${sug.nombre}, no en ${elegido}.\n\nPodés estar en el límite entre los dos barrios. Si conocés la zona y sabés que es ${elegido}, guardá igual.\n\nOK = guardar en ${elegido}\nCancelar = revisar antes`
        : `OpenStreetMap detectó algo distinto: ${barrioMismatch.mensaje}\n\n¿Guardás igual?`
      if (!confirm(msg)) return
    }
    setSaving(true)
    try {
      if (!titular) throw new Error('Falta cargar quien paga (titular)')
      if (!mismoBenef && !beneficiario) throw new Error('Destildaste "el mismo" pero no cargaste al beneficiario')
      if (!form.barrioId || !form.direccion.trim()) throw new Error('Falta elegir barrio y dirección')
      if (form.diaEntrega && !diasDisponibles.includes(form.diaEntrega)) {
        throw new Error('El día elegido no está disponible para ese barrio')
      }
      const r = await crearSuscripcion({
        titularId: titular.id,
        beneficiarioId: mismoBenef ? null : beneficiario?.id,
        alias: form.alias,
        barrioId: form.barrioId,
        barrioNombre: barrioSeleccionado.nombre,
        direccion: form.direccion,
        apto: form.apto || null,
        tipoVivienda: form.tipoVivienda || null,
        kitCodigo: form.kitCodigo || null,
        diaEntrega: form.diaEntrega || null,
        ventanaHoraria: form.ventanaHoraria,
        semanaMes: form.semanaMes || null,
        diaCobroMes: form.diaCobroMes ? Number(form.diaCobroMes) : null,
        modeloCobro: form.modeloCobro,
        porteroReferidoId: form.porteroReferidoId || null,
        notas: form.notas,
        coordsManual: coords,  // las coords del pin (override del geocoding automático)
      })
      onCreated(r)
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <h2 style={{ margin: '0 0 6px' }}>Nueva suscripción</h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 18 }}>
          Cargá quién paga, quién recibe (si es la misma marcá la casilla) y los datos de entrega.
        </p>

        {/* TITULAR */}
        <h3 className="section-head" style={{ marginTop: 0 }}>💳 Quien paga la suscripción</h3>
        <BuscarPersona seleccionada={titular} onSelect={setTitular} esTitular={true} />

        {titular && (
          <>
            {/* CHECKBOX MISMO */}
            <div style={{ marginTop: 18, padding: '12px 14px', background: mismoBenef ? '#ECFDF5' : '#FEF3C7', border: `2px solid ${mismoBenef ? '#10B981' : '#F59E0B'}`, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                <input
                  type="checkbox"
                  checked={mismoBenef}
                  onChange={e => { setMismoBenef(e.target.checked); if (e.target.checked) setBeneficiario(null) }}
                  style={{ width: 18, height: 18 }}
                />
                <span>📦 El beneficiario es el mismo que el titular</span>
              </label>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, paddingLeft: 28 }}>
                {mismoBenef
                  ? <>Va a recibir <strong>{titular.nombre}</strong> en la dirección que cargues abajo.</>
                  : <>Tildá la casilla si quien recibe es la misma persona que paga, o cargá abajo a quién recibe (regalo, familiar, etc.)</>}
              </div>
            </div>

            {/* BENEFICIARIO (solo si destildó) */}
            {!mismoBenef && (
              <div style={{ marginTop: 14 }}>
                <h3 className="section-head" style={{ marginTop: 0 }}>🎁 Quien recibe el pedido</h3>
                <BuscarPersona seleccionada={beneficiario} onSelect={setBeneficiario} esTitular={false} />
              </div>
            )}

            {/* DATOS SUSCRIPCIÓN */}
            <h3 className="section-head" style={{ marginTop: 22 }}>📦 Datos de la suscripción</h3>
            <Field label="Alias (para distinguir si tiene varias)">
              <input type="text" value={form.alias} onChange={e => setForm({...form, alias: e.target.value})} placeholder="Ej: Casa, Mamá, Oficina" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Barrio" required>
                <select
                  required
                  value={form.barrioId}
                  onChange={e => setForm({...form, barrioId: e.target.value, diaEntrega: ''})}
                >
                  <option value="">— elegir barrio —</option>
                  {barrios.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
              </Field>
              <Field label="Tipo">
                <select value={form.tipoVivienda} onChange={e => setForm({...form, tipoVivienda: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="casa">🏠 Casa</option>
                  <option value="edificio">🏢 Edificio</option>
                  <option value="oficina">🏢 Oficina</option>
                </select>
              </Field>
            </div>
            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '6px 10px', fontSize: 11, marginTop: -6, marginBottom: 10, color: '#92400E' }}>
              ¿No está el barrio del cliente? Lamentablemente <strong>no estamos en esa zona todavía</strong>. Decile que próximamente evaluaremos cubrirla.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <Field label="Calle + número" required>
                <input
                  type="text" required
                  value={form.direccion}
                  onChange={e => setForm({...form, direccion: e.target.value})}
                  onBlur={geocodearDireccion}
                  placeholder="18 de Julio 1234"
                />
              </Field>
              <Field label="Apto / Piso / Casa">
                <input
                  type="text"
                  value={form.apto}
                  onChange={e => setForm({...form, apto: e.target.value})}
                  placeholder="Apto 406"
                />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: -6, marginBottom: 12 }}>
              <button
                type="button"
                onClick={geocodearDireccion}
                disabled={!form.barrioId || geocodando}
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: '#1A3461', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}
              >
                {geocodando ? '⏳ Buscando…' : coords ? '🗺️ Recargar mapa' : '🗺️ Abrir mapa'}
              </button>
              {coords && coordsExactas && <span style={{ fontSize: 11, color: '#065F46', fontWeight: 700 }}>✓ pin confirmado</span>}
              {coords && !coordsExactas && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 700 }}>⚠️ verificá el pin abajo</span>}
              {geoErr && <span style={{ fontSize: 11, color: '#92400E' }}>{geoErr}</span>}
            </div>

            {coords && (
              <>
                {barrioMismatch && !barrioMismatch.ok && (
                  barrioMismatch.sinCobertura ? (
                    /* BLOQUEO DURO: rojo + botón WhatsApp al equipo */
                    <div style={{
                      padding: '14px 16px', marginBottom: 8,
                      background: '#FEE2E2', border: '2px solid #DC2626',
                      borderRadius: 10, fontSize: 13, color: '#991B1B',
                    }}>
                      <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 14 }}>
                        🚫 Fuera de zona de cobertura
                      </div>
                      <div style={{ marginBottom: 8 }}>{barrioMismatch.mensaje}</div>
                      <div style={{ fontSize: 12, marginBottom: 10 }}>
                        No se puede guardar este cliente. Avisale al equipo para evaluar si vale la pena sumar el barrio nuevo.
                      </div>
                      {/*
                        Escalado interno → WA_NUMERO (Nico, 098 610 001).
                        TODO post-lanzamiento: pasar a canal Slack/grupo interno
                        cuando se delegue la decisión de barrios nuevos a Gimena.
                        Mantenemos WhatsApp por ahora porque es el más usado por el equipo.
                      */}
                      <a
                        href={`https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(
                          `🚨 [INTERNO] Posible cliente fuera de cobertura\n\n` +
                          `📍 Dirección: ${form.direccion || '(sin dirección)'}\n` +
                          `🏘 Barrio elegido: ${barrioSeleccionado?.nombre || '—'}\n` +
                          `🗺 OSM detectó: ${barrioMismatch.mensaje}\n\n` +
                          `¿Evaluamos sumar la zona? Avisame y armo el alta del barrio nuevo.`
                        )}`}
                        target="_blank"
                        rel="noopener"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '10px 16px', background: '#25D366', color: '#fff',
                          textDecoration: 'none', borderRadius: 8,
                          fontSize: 13, fontWeight: 800,
                          boxShadow: '0 2px 6px rgba(37,211,102,.35)',
                        }}
                      >
                        💬 Avisar al equipo por WhatsApp
                      </a>
                    </div>
                  ) : (
                    /* AVISO SUAVE: amarillo, permite guardar con confirm */
                    <div style={{
                      padding: '12px 14px', marginBottom: 8,
                      background: '#FEF3C7', border: '1.5px solid #F59E0B',
                      borderRadius: 8, fontSize: 13, color: '#92400E',
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>
                        ⚠️ Posible mismatch de barrio
                      </div>
                      <div>{barrioMismatch.mensaje}</div>
                      <div style={{ fontSize: 11, color: '#78350F', marginTop: 6, fontStyle: 'italic' }}>
                        OpenStreetMap se equivoca seguido cerca de límites. Si vos sabés que la dirección está bien, guardá igual.
                      </div>
                      {barrioMismatch.sugerenciaActivo && (
                        <button
                          type="button"
                          onClick={() => {
                            setForm({ ...form, barrioId: barrioMismatch.sugerenciaActivo.id })
                            setBarrioMismatch(null)
                          }}
                          style={{
                            marginTop: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700,
                            background: '#1A3461', color: '#fff', border: 0,
                            borderRadius: 6, cursor: 'pointer',
                          }}
                        >
                          ✓ Cambiar a {barrioMismatch.sugerenciaActivo.nombre}
                        </button>
                      )}
                    </div>
                  )
                )}
                <MapPin
                  lat={coords.lat}
                  lng={coords.lng}
                  direccion={`${form.direccion}, ${barrioSeleccionado?.nombre || ''}`}
                  onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }}
                  height={260}
                />
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  marginTop: 8, padding: '10px 14px', borderRadius: 8,
                  border: coordsExactas ? '2px solid #10B981' : '2px solid #F59E0B',
                  background: coordsExactas ? '#ECFDF5' : '#FEF3C7',
                  fontSize: 13, fontWeight: 600,
                }}>
                  <input
                    type="checkbox"
                    checked={coordsExactas}
                    onChange={e => setCoordsExactas(e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>{coordsExactas ? '✅ El pin está en la ubicación correcta' : '⚠️ Confirmá que el pin está en el lugar correcto'}</span>
                </label>
              </>
            )}

            <Field label="Kit">
              <select value={form.kitCodigo} onChange={e => setForm({...form, kitCodigo: e.target.value})}>
                <option value="">— sin kit —</option>
                {kits.map(k => <option key={k.codigo} value={k.codigo}>{k.nombre}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                💡 A futuro: pantalla visual de comparación de kits (con fotos + contenido + precio).
              </div>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Día de entrega" required={!!form.barrioId}>
                <select
                  value={form.diaEntrega}
                  onChange={e => setForm({...form, diaEntrega: e.target.value})}
                  disabled={!form.barrioId}
                >
                  <option value="">{form.barrioId ? '— elegir día —' : '— elegí barrio primero —'}</option>
                  {diasDisponibles.map(d =>
                    <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>
                  )}
                </select>
                {form.barrioId && diasDisponibles.length === 0 && (
                  <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>Este barrio no tiene días configurados</div>
                )}
              </Field>
              <Field label="Semana del mes" required>
                <select value={form.semanaMes} onChange={e => setForm({...form, semanaMes: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="1">1ra (días 1-7)</option>
                  <option value="2">2da (días 8-14)</option>
                  <option value="3">3ra (días 15-21)</option>
                  <option value="4">4ta (días 22-31)</option>
                </select>
              </Field>
              <Field label="Ventana">
                <select value={form.ventanaHoraria} onChange={e => setForm({...form, ventanaHoraria: e.target.value})}>
                  {VENTANAS_HORARIAS.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Portero referido (si vino por uno)">
              <select value={form.porteroReferidoId} onChange={e => setForm({...form, porteroReferidoId: e.target.value})}>
                <option value="">— directo, sin referido —</option>
                {porteros.map(p => <option key={p.id} value={p.id}>{p.lugar} · {p.profile?.nombre}</option>)}
              </select>
            </Field>

            <Field label="Modelo de cobro" required>
              <select value={form.modeloCobro} onChange={e => setForm({...form, modeloCobro: e.target.value})}>
                <option value="prepaid_mensiversario">🔄 Mensiversario (cliente normal — paga siempre el mismo día del mes)</option>
                <option value="postpaid_entrega">🤝 Al entregar (amigos/familia — pagás cuando recibís)</option>
                <option value="gratis">🎁 Gratis (tester / regalo — no se cobra)</option>
              </select>
            </Field>

            {form.modeloCobro === 'prepaid_mensiversario' && (
              <Field label="Día del mes en que se cobra (1-31)" required>
                <input
                  type="number" min="1" max="31"
                  value={form.diaCobroMes}
                  onChange={e => setForm({...form, diaCobroMes: e.target.value})}
                  placeholder="15"
                />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  💡 Default: hoy. Es el día del mes en que se le cobra todos los meses.
                </div>
              </Field>
            )}

            <Field label="Notas (instrucciones de entrega, etc.)">
              <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" placeholder="Llamar al timbre 8B..." />
            </Field>
          </>
        )}

        {err && <div className="err-box" style={{ marginTop: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 18, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSubmit} disabled={saving || !titular}>
            {saving ? 'Guardando…' : '✓ Crear suscripción'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* Buscar persona o crear nueva.
   Si esTitular = true → más datos obligatorios (CI/RUT/email).
   Si esTitular = false → solo nombre + teléfono obligatorios (es solo destinatario). */
function BuscarPersona({ seleccionada, onSelect, esTitular = true }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [showCrear, setShowCrear] = useState(false)

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return }
    setSearching(true)
    const t = setTimeout(async () => {
      const r = await buscarPersonas(q)
      setResults(r); setSearching(false)
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  if (seleccionada) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, border: '2px solid #10B981', background: '#ECFDF5', borderRadius: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700 }}>✓ {seleccionada.nombre}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {seleccionada.documento && <span>{seleccionada.documento_tipo?.toUpperCase()} {seleccionada.documento}</span>}
            {seleccionada.phone && <span>📞 {seleccionada.phone}</span>}
            {seleccionada.email && <span>✉️ {seleccionada.email}</span>}
          </div>
        </div>
        <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onSelect(null)}>Cambiar</button>
      </div>
    )
  }

  if (showCrear) {
    return <FormCrearPersona
      esTitular={esTitular}
      onCancel={() => setShowCrear(false)}
      onCreated={p => { onSelect(p); setShowCrear(false) }}
    />
  }

  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Buscar por nombre, teléfono, email o documento…"
        autoFocus
        style={{ width: '100%', padding: 10, fontSize: 14, borderRadius: 8, border: '1px solid var(--line)' }}
      />
      {searching && <div style={{ padding: 8, fontSize: 12, color: 'var(--muted)' }}>Buscando…</div>}
      {results.length > 0 && (
        <div style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          {results.map(r => (
            <div
              key={r.id}
              onClick={() => onSelect(r)}
              style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--line)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--paper)'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 700 }}>{r.nombre}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {r.documento && <span>{r.documento_tipo?.toUpperCase()} {r.documento}</span>}
                {r.phone && <span>📞 {r.phone}</span>}
                {r.email && <span>✉️ {r.email}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => setShowCrear(true)}
        style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, border: '1.5px dashed var(--navy)', background: '#fff', color: 'var(--navy)', fontWeight: 700, fontSize: 13, cursor: 'pointer', width: '100%' }}
      >+ Crear persona nueva</button>
    </div>
  )
}

/* Sub-form para crear persona con validación de documento + detector de duplicados */
function FormCrearPersona({ esTitular, onCancel, onCreated }) {
  const [f, setF] = useState({
    nombre: '', phone: '', email: '',
    documentoTipo: 'ci', documento: '',
  })
  const [docErr, setDocErr] = useState(null)
  const [duplicado, setDuplicado] = useState(null)
  const [emailErr, setEmailErr] = useState(null)
  const [telErr, setTelErr] = useState(null)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)

  // Detección de duplicado por documento (con debounce)
  useEffect(() => {
    setDuplicado(null); setDocErr(null)
    if (!f.documento.trim()) return
    const v = validarDocumento(f.documentoTipo, f.documento)
    if (!v.ok) { setDocErr(v.msg); return }
    const t = setTimeout(async () => {
      const dup = await buscarPorDocumento(f.documentoTipo, v.normalizado)
      if (dup) setDuplicado(dup)
    }, 400)
    return () => clearTimeout(t)
  }, [f.documento, f.documentoTipo])

  // Validar email on blur
  function checkEmail() {
    if (!f.email.trim()) { setEmailErr(null); return }
    setEmailErr(validarEmail(f.email) ? null : 'Email inválido')
  }
  function checkTel() {
    if (!f.phone.trim()) { setTelErr(null); return }
    setTelErr(validarTelUY(f.phone) ? null : 'Teléfono uruguayo inválido (ej: 098 123 456)')
  }

  async function handleCrear() {
    setErr(null)
    if (!f.nombre.trim()) { setErr('Falta el nombre'); return }
    if (esTitular) {
      if (!f.documento.trim()) { setErr('Para el titular es obligatorio el documento'); return }
      // docErr es solo warning, NO bloquea
      if (!f.email.trim()) { setErr('Para el titular es obligatorio el email (para facturas)'); return }
      if (!validarEmail(f.email)) { setErr('Email inválido'); return }
    }
    if (!f.phone.trim()) { setErr('El teléfono es obligatorio (para WhatsApp)'); return }
    if (!validarTelUY(f.phone)) { setErr('Teléfono uruguayo inválido'); return }
    // Warning del documento NO bloquea el guardado

    setCreando(true)
    try {
      const v = validarDocumento(f.documentoTipo, f.documento)
      const p = await crearPersona({
        nombre: f.nombre,
        phone: formatearTelUY(f.phone),
        email: f.email,
        documento: v.normalizado,
        documentoTipo: f.documento.trim() ? f.documentoTipo : null,
      })
      onCreated(p)
    } catch (e) {
      const msg = String(e.message || '')
      if (msg.includes('profiles_documento_unq')) setErr('Ya existe alguien con ese documento. Buscá arriba en la lista.')
      else if (msg.includes('profiles_email_unq')) setErr('Ya existe alguien con ese email.')
      else setErr(msg)
    }
    finally { setCreando(false) }
  }

  return (
    <div style={{ background: 'var(--paper)', padding: 14, borderRadius: 8 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>Crear persona nueva</h4>

      <Field label="Nombre completo" required>
        <input type="text" value={f.nombre} onChange={e => setF({...f, nombre: e.target.value})} placeholder="María Fernández" autoFocus />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8 }}>
        <Field label="Tipo doc">
          <select value={f.documentoTipo} onChange={e => setF({...f, documentoTipo: e.target.value, documento: ''})}>
            <option value="ci">CI</option>
            <option value="rut">RUT</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </Field>
        <Field label={esTitular ? 'Documento' : 'Documento (opcional)'} required={esTitular}>
          <input
            type="text"
            value={f.documento}
            onChange={e => setF({...f, documento: e.target.value})}
            onBlur={e => {
              // Auto-format on blur: si valida, lo dejo lindo
              const v = e.target.value
              if (!v) return
              if (f.documentoTipo === 'ci' && validarCI(v)) {
                setF(prev => ({ ...prev, documento: formatearCI(v) }))
              }
            }}
            placeholder={f.documentoTipo === 'ci' ? 'Ej: 36955508' : f.documentoTipo === 'rut' ? '212345670012' : 'AB123456'}
            style={{ borderColor: docErr ? '#F59E0B' : undefined }}
            inputMode={f.documentoTipo === 'pasaporte' ? 'text' : 'numeric'}
          />
          {f.documentoTipo === 'ci' && !docErr && (
            <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>
              7 u 8 dígitos, con o sin puntos y guión
            </div>
          )}
          {docErr && (
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 3, background: '#FEF3C7', padding: '4px 6px', borderRadius: 4 }}>
              ⚠️ {docErr}. Si estás seguro de que es correcto, podés guardar igual.
            </div>
          )}
        </Field>
      </div>

      {duplicado && (
        <div style={{ background: '#FEF3C7', border: '1.5px solid #F59E0B', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
          ⚠️ Ya existe <strong>{duplicado.nombre}</strong> con ese documento.
          <button
            onClick={() => onCreated(duplicado)}
            style={{ marginLeft: 8, padding: '4px 10px', background: '#F59E0B', color: '#fff', border: 0, borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
          >Usar esta persona →</button>
        </div>
      )}

      <Field label={esTitular ? 'Email (para facturas)' : 'Email (opcional)'} required={esTitular}>
        <input
          type="email"
          value={f.email}
          onChange={e => setF({...f, email: e.target.value})}
          onBlur={checkEmail}
          placeholder="maria@ejemplo.com"
          style={{ borderColor: emailErr ? '#EF4444' : undefined }}
        />
        {emailErr && <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>{emailErr}</div>}
      </Field>

      <Field label="Teléfono móvil" required>
        <input
          type="tel"
          value={f.phone}
          onChange={e => setF({...f, phone: e.target.value})}
          onBlur={checkTel}
          placeholder="098 123 456"
          style={{ borderColor: telErr ? '#EF4444' : undefined }}
        />
        {telErr && <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>{telErr}</div>}
      </Field>

      {err && <div className="err-box" style={{ marginTop: 6 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn ghost" onClick={onCancel}>Cancelar</button>
        <button className="btn primary" onClick={handleCrear} disabled={creando || !f.nombre.trim()}>
          {creando ? 'Creando…' : '+ Crear'}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}

/* ───────── Modal de edición de suscripción + persona ───────── */

function ModalEditar({ suscripcion: s, onClose, onSaved }) {
  const [tab, setTab] = useState('suscripcion')  // suscripcion | titular | beneficiario
  const [barrios, setBarrios] = useState([])
  const [kits, setKits] = useState([])
  const [porteros, setPorteros] = useState([])

  // Estado del form de suscripción
  const [form, setForm] = useState({
    alias: s.alias || '',
    barrioId: s.barrio_obj?.id || '',
    direccion: s.direccion || '',
    apto: s.apto || '',
    tipoVivienda: s.tipo_vivienda || '',
    kitCodigo: s.kit_actual || '',
    diaEntrega: s.dia_entrega || '',
    ventanaHoraria: s.ventana_horaria || 'manana',
    semanaMes: s.semana_mes || '',
    diaCobroMes: s.dia_cobro_mes || '',
    modeloCobro: s.modelo_cobro || 'prepaid_mensiversario',
    porteroReferidoId: s.portero?.id || '',
    notas: s.notas || '',
  })
  const [coords, setCoords] = useState(s.lat ? { lat: Number(s.lat), lng: Number(s.lng) } : null)
  const [coordsExactas, setCoordsExactas] = useState(true)  // si tiene coords ya, asumimos OK
  const [geocodando, setGeocodando] = useState(false)
  const [geoErr, setGeoErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const barrioSeleccionado = useMemo(() => barrios.find(b => b.id === form.barrioId), [barrios, form.barrioId])
  const diasDisponibles = barrioSeleccionado?.dias || []

  useEffect(() => {
    Promise.all([getKits(), getPorterosActivos(), getBarrios()]).then(([k, p, b]) => {
      setKits(k); setPorteros(p); setBarrios(b)
    })
  }, [])

  async function geocodear() {
    setGeoErr(null)
    setCoordsExactas(false)
    const barrioNombre = barrioSeleccionado?.nombre
    if (!barrioNombre) return
    setGeocodando(true)
    try {
      if (form.direccion.trim()) {
        const r = await geocodificar(`${form.direccion}, ${barrioNombre}`)
        if (r) { setCoords({ lat: r.lat, lng: r.lng }); setCoordsExactas(true); return }
      }
      const rb = await geocodificar(barrioNombre)
      if (rb) {
        setCoords({ lat: rb.lat, lng: rb.lng })
        setGeoErr('No encontré la dirección exacta. Movelo en el mapa.')
      }
    } finally { setGeocodando(false) }
  }

  async function guardarSuscripcion() {
    setErr(null); setSaving(true)
    try {
      if (!form.barrioId || !form.direccion.trim()) throw new Error('Falta barrio y dirección')
      await actualizarSuscripcion(s.id, {
        alias: form.alias?.trim() || null,
        barrio_id: form.barrioId,
        barrio: barrioSeleccionado?.nombre || s.barrio,
        direccion: form.direccion.trim(),
        apto: form.apto?.trim() || null,
        tipo_vivienda: form.tipoVivienda || null,
        kit_actual: form.kitCodigo || null,
        dia_entrega: form.diaEntrega || null,
        ventana_horaria: form.ventanaHoraria,
        semana_mes: form.semanaMes || null,
        dia_cobro_mes: form.diaCobroMes ? Number(form.diaCobroMes) : null,
        modelo_cobro: form.modeloCobro,
        portero_referido_id: form.porteroReferidoId || null,
        notas: form.notas?.trim() || null,
      }, { coordsManual: coords })
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: '0 0 2px' }}>Editar suscripción</h2>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {(s.beneficiario || s.titular)?.nombre} · {s.barrio_obj?.nombre || s.barrio}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '4px 10px' }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          {[
            { id: 'suscripcion', label: '📦 Suscripción' },
            { id: 'titular', label: '💳 Titular' },
            { id: 'beneficiario', label: '🎁 Beneficiario', disabled: !s.beneficiario },
            { id: 'cobros', label: '💰 Cobros' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              style={{
                padding: '10px 16px', border: 0, background: 'transparent',
                fontSize: 13, fontWeight: 700, cursor: t.disabled ? 'not-allowed' : 'pointer',
                color: t.disabled ? '#ccc' : tab === t.id ? 'var(--navy)' : 'var(--muted)',
                borderBottom: tab === t.id ? '3px solid var(--navy)' : '3px solid transparent',
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>

        {tab === 'suscripcion' && (
          <>
            <Field label="Alias">
              <input type="text" value={form.alias} onChange={e => setForm({...form, alias: e.target.value})} placeholder="Casa, Mamá, Oficina" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Barrio" required>
                <select required value={form.barrioId} onChange={e => setForm({...form, barrioId: e.target.value, diaEntrega: ''})}>
                  <option value="">— elegir —</option>
                  {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Field>
              <Field label="Tipo">
                <select value={form.tipoVivienda} onChange={e => setForm({...form, tipoVivienda: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="casa">🏠 Casa</option>
                  <option value="edificio">🏢 Edificio</option>
                  <option value="oficina">🏢 Oficina</option>
                </select>
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <Field label="Calle + número" required>
                <input type="text" required value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} placeholder="18 de Julio 1234" />
              </Field>
              <Field label="Apto / Piso">
                <input type="text" value={form.apto} onChange={e => setForm({...form, apto: e.target.value})} placeholder="Apto 406" />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: '#1A3461', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}>
                {geocodando ? '⏳' : coords ? '🗺️ Recargar mapa' : '🗺️ Abrir mapa'}
              </button>
              {coords && coordsExactas && <span style={{ fontSize: 11, color: '#065F46', fontWeight: 700 }}>✓ pin OK</span>}
              {coords && !coordsExactas && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 700 }}>⚠️ verificá el pin</span>}
              {geoErr && <span style={{ fontSize: 11, color: '#92400E' }}>{geoErr}</span>}
            </div>
            {coords && (
              <>
                <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }} height={220} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 8, padding: '10px 14px', borderRadius: 8, border: coordsExactas ? '2px solid #10B981' : '2px solid #F59E0B', background: coordsExactas ? '#ECFDF5' : '#FEF3C7', fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} style={{ width: 18, height: 18 }} />
                  <span>{coordsExactas ? '✅ Pin en la ubicación correcta' : '⚠️ Confirmá el pin'}</span>
                </label>
              </>
            )}

            <h3 className="section-head" style={{ marginTop: 16 }}>Operación</h3>
            <Field label="Kit">
              <select value={form.kitCodigo} onChange={e => setForm({...form, kitCodigo: e.target.value})}>
                <option value="">— sin kit —</option>
                {kits.map(k => <option key={k.codigo} value={k.codigo}>{k.nombre}</option>)}
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Día">
                <select value={form.diaEntrega} onChange={e => setForm({...form, diaEntrega: e.target.value})} disabled={!form.barrioId}>
                  <option value="">— elegir —</option>
                  {diasDisponibles.map(d => <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>)}
                </select>
              </Field>
              <Field label="Semana mes">
                <select value={form.semanaMes} onChange={e => setForm({...form, semanaMes: e.target.value})}>
                  <option value="">—</option>
                  <option value="1">1ra</option>
                  <option value="2">2da</option>
                  <option value="3">3ra</option>
                  <option value="4">4ta</option>
                </select>
              </Field>
              <Field label="Ventana">
                <select value={form.ventanaHoraria} onChange={e => setForm({...form, ventanaHoraria: e.target.value})}>
                  {VENTANAS_HORARIAS.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Portero referido">
              <select value={form.porteroReferidoId} onChange={e => setForm({...form, porteroReferidoId: e.target.value})}>
                <option value="">— directo —</option>
                {porteros.map(p => <option key={p.id} value={p.id}>{p.lugar} · {p.profile?.nombre}</option>)}
              </select>
            </Field>

            <h3 className="section-head" style={{ marginTop: 16 }}>💰 Cobro</h3>
            <Field label="Modelo de cobro" required>
              <select value={form.modeloCobro} onChange={e => setForm({...form, modeloCobro: e.target.value})}>
                <option value="prepaid_mensiversario">🔄 Mensiversario (paga el mismo día cada mes)</option>
                <option value="postpaid_entrega">🤝 Al entregar (amigos/familia)</option>
                <option value="gratis">🎁 Gratis (no se cobra)</option>
              </select>
            </Field>
            {form.modeloCobro === 'prepaid_mensiversario' && (
              <Field label="Día del mes en que se cobra (1-31)" required>
                <input type="number" min="1" max="31" value={form.diaCobroMes} onChange={e => setForm({...form, diaCobroMes: e.target.value})} placeholder="15" />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  Si elegís 31 y el mes tiene 30 días, cobra el 30.
                </div>
              </Field>
            )}

            <Field label="Notas">
              <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" />
            </Field>

            {err && <div className="err-box">{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <button className="btn ghost" onClick={onClose}>Cancelar</button>
              <button className="btn primary" onClick={guardarSuscripcion} disabled={saving}>
                {saving ? 'Guardando…' : '✓ Guardar cambios'}
              </button>
            </div>
          </>
        )}

        {tab === 'titular' && s.titular && (
          <FormEditarPersona persona={s.titular} onSaved={onSaved} onClose={onClose} esTitular={true} />
        )}

        {tab === 'beneficiario' && s.beneficiario && (
          <FormEditarPersona persona={s.beneficiario} onSaved={onSaved} onClose={onClose} esTitular={false} />
        )}

        {tab === 'cobros' && (
          <TabCobros suscripcion={s} onClose={onClose} />
        )}
      </div>
    </div>
  )
}

/* ───────── Tab Cobros del cliente ───────── */
function TabCobros({ suscripcion: s, onClose }) {
  const [pagos, setPagos] = useState([])
  const [cuenta, setCuenta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRegistrar, setShowRegistrar] = useState(null)  // pago a marcar como pagado
  const [showCrearManual, setShowCrearManual] = useState(false)

  useEffect(() => { load() }, [s.id])

  async function load() {
    setLoading(true)
    const [p, c] = await Promise.all([getPagosCliente(s.id), getCuentaCobro()])
    setPagos(p); setCuenta(c)
    setLoading(false)
  }

  // Próximo pago pendiente y stats
  const pendientes = pagos.filter(p => p.estado === 'pendiente' || p.estado === 'atrasado')
  const proximo = pendientes[0]
  const totalAlDia = pagos.filter(p => p.estado === 'pagado').length
  const totalPendiente = pendientes.length

  // Texto para WhatsApp del próximo pago
  function abrirWA(pago) {
    // Normalización consistente: removemos todo no-dígito incluyendo +598, luego forzamos 598 prefix.
    const raw = (s.titular?.phone || '').replace(/\D/g, '')
    const sinCero = raw.replace(/^0+/, '')           // 098xxx → 98xxx
    const sin598  = sinCero.replace(/^598/, '')       // 5985989xxx → 5989xxx (sacamos el 598 si ya estaba)
    const telWa   = sin598 ? '598' + sin598 : null
    if (!telWa) { alert('El titular no tiene teléfono cargado'); return }
    const texto = textoWhatsAppCobro({ cliente: s, pago, cuenta })
    const url = `https://wa.me/${telWa}?text=${encodeURIComponent(texto)}`
    window.open(url, '_blank')
  }

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>

  return (
    <>
      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ padding: 14, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#065F46', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>Pagados</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#065F46' }}>{totalAlDia}</div>
        </div>
        <div style={{ padding: 14, background: totalPendiente > 0 ? '#FEF3C7' : '#F1F5F9', border: `1px solid ${totalPendiente > 0 ? '#F59E0B' : '#cbd5e1'}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#92400E', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>Pendientes</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: totalPendiente > 0 ? '#92400E' : '#475569' }}>{totalPendiente}</div>
        </div>
      </div>

      {/* Próximo cobro destacado */}
      {proximo && (
        <div style={{ background: 'linear-gradient(135deg, #00A8B5 0%, #1A3461 100%)', color: '#fff', padding: 16, borderRadius: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.15em', opacity: .85, marginBottom: 4 }}>PRÓXIMO COBRO</div>
          <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
            ${Number(proximo.monto).toLocaleString('es-UY')}
          </div>
          <div style={{ fontSize: 13, opacity: .9 }}>
            Vence: {proximo.vence ? new Date(proximo.vence + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}
            {proximo.entrega?.numero_orden && <> · Orden {proximo.entrega.numero_orden}</>}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <button onClick={() => abrirWA(proximo)} style={{ background: '#25D366', color: '#fff', border: 0, padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              💬 Mandar WA al cliente
            </button>
            <button onClick={() => setShowRegistrar(proximo)} style={{ background: '#fff', color: '#1A3461', border: 0, padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              ✓ Marcar pagado
            </button>
          </div>
        </div>
      )}

      {/* Aviso si no hay cuenta configurada */}
      {!cuenta?.banco && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: '#92400E' }}>
          ⚠️ Configurá tu cuenta bancaria en <strong>Admin → Configuración</strong> para que aparezca en el WA al cliente.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 className="section-head" style={{ margin: 0 }}>Historial de pagos</h3>
        <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setShowCrearManual(true)}>+ Cobro manual</button>
      </div>

      {pagos.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          No hay pagos todavía. Se generan automáticamente al confirmar entregas.
        </div>
      ) : (
        <table className="t-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Vence</th>
              <th>Monto</th>
              <th>Método</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pagos.map(p => {
              const cfg = ESTADO_PAGO_COLOR[p.estado] || ESTADO_PAGO_COLOR.pendiente
              return (
                <tr key={p.id}>
                  <td>
                    {p.vence ? new Date(p.vence + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                    {p.entrega?.numero_orden && <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.entrega.numero_orden}</div>}
                  </td>
                  <td style={{ fontWeight: 700 }}>${Number(p.monto).toLocaleString('es-UY')}</td>
                  <td style={{ textTransform: 'capitalize', fontSize: 11, color: 'var(--muted)' }}>{p.metodo || '—'}</td>
                  <td>
                    <span style={{ background: cfg.bg, color: cfg.text, padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      ● {ESTADO_PAGO_LABEL[p.estado]}
                    </span>
                    {p.pagado_at && <div style={{ fontSize: 10, color: '#065F46', marginTop: 2 }}>{new Date(p.pagado_at).toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })}</div>}
                  </td>
                  <td>
                    {(p.estado === 'pendiente' || p.estado === 'atrasado') && (
                      <button onClick={() => setShowRegistrar(p)} style={{ padding: '3px 8px', fontSize: 10, background: '#10B981', color: '#fff', border: 0, borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}>✓ Pagado</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showRegistrar && (
        <ModalRegistrarPago pago={showRegistrar} onClose={() => setShowRegistrar(null)} onDone={() => { setShowRegistrar(null); load() }} />
      )}

      {showCrearManual && (
        <ModalCrearPagoManual clienteId={s.id} kit={s.kit} onClose={() => setShowCrearManual(false)} onDone={() => { setShowCrearManual(false); load() }} />
      )}
    </>
  )
}

function ModalRegistrarPago({ pago, onClose, onDone }) {
  const [metodo, setMetodo] = useState(pago.metodo || 'transferencia')
  const [referencia, setReferencia] = useState('')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      await registrarPago(pago.id, { metodo, referencia, notas })
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ ...modalBg, zIndex: 1100 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>Marcar como pagado</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0 }}>
          Monto: <strong>${Number(pago.monto).toLocaleString('es-UY')}</strong>
        </p>
        <Field label="Método">
          <select value={metodo} onChange={e => setMetodo(e.target.value)}>
            <option value="transferencia">Transferencia bancaria</option>
            <option value="efectivo">Efectivo</option>
            <option value="mercadopago">MercadoPago</option>
            <option value="handy">Handy (legacy)</option>
            <option value="otro">Otro</option>
          </select>
        </Field>
        <Field label="Nro de referencia / comprobante">
          <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="Nº de transferencia" />
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : '✓ Confirmar pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalCrearPagoManual({ clienteId, kit, onClose, onDone }) {
  const [monto, setMonto] = useState(kit?.precio_susc || '')
  const [vence, setVence] = useState(new Date().toISOString().slice(0, 10))
  const [metodo, setMetodo] = useState('transferencia')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      if (!monto || Number(monto) <= 0) throw new Error('Monto inválido')
      await crearPagoManual({
        clienteId,
        monto,
        mes: vence.slice(0, 7) + '-01',  // primer día del mes del vencimiento
        vence,
        metodo,
        notas,
      })
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ ...modalBg, zIndex: 1100 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>+ Cobro manual</h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0 }}>
          Para retroactivos, extras o casos especiales fuera del flujo automático.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Monto" required>
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="1500" />
          </Field>
          <Field label="Vence" required>
            <input type="date" value={vence} onChange={e => setVence(e.target.value)} />
          </Field>
        </div>
        <Field label="Método">
          <select value={metodo} onChange={e => setMetodo(e.target.value)}>
            <option value="transferencia">Transferencia</option>
            <option value="efectivo">Efectivo</option>
            <option value="mercadopago">MercadoPago</option>
            <option value="handy">Handy (legacy)</option>
            <option value="otro">Otro</option>
          </select>
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" placeholder="Por ejemplo: kit de prueba, reposición, etc." />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : '+ Crear cobro'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* Form para editar datos de una persona (titular o beneficiario) */
function FormEditarPersona({ persona, onSaved, onClose, esTitular }) {
  const [f, setF] = useState({
    nombre: persona.nombre || '',
    phone: persona.phone || '',
    email: persona.email || '',
    documento: persona.documento || '',
    documento_tipo: persona.documento_tipo || 'ci',
    transferencia_habilitada: !!persona.transferencia_habilitada,
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      if (!f.nombre.trim()) throw new Error('Falta el nombre')
      const cambioTransf = f.transferencia_habilitada !== !!persona.transferencia_habilitada
      const payload = { ...f }
      if (cambioTransf) {
        payload.transferencia_habilitada_at = f.transferencia_habilitada ? new Date().toISOString() : null
      }
      await actualizarPersona(persona.id, payload)
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <>
      <Field label="Nombre" required>
        <input type="text" value={f.nombre} onChange={e => setF({...f, nombre: e.target.value})} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8 }}>
        <Field label="Tipo doc">
          <select value={f.documento_tipo} onChange={e => setF({...f, documento_tipo: e.target.value})}>
            <option value="ci">CI</option>
            <option value="rut">RUT</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </Field>
        <Field label="Documento">
          <input type="text" value={f.documento} onChange={e => setF({...f, documento: e.target.value})} />
        </Field>
      </div>
      <Field label="Email">
        <input type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} />
      </Field>
      <Field label="Teléfono">
        <input type="tel" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} />
      </Field>

      {/* ───────────── Permisos especiales ───────────── */}
      <div style={{ marginTop: 18, padding: 14, background: '#F8FAFC', border: '1px solid var(--line)', borderRadius: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
          🔑 Permisos especiales
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
          <input
            type="checkbox"
            checked={f.transferencia_habilitada}
            onChange={e => setF({ ...f, transferencia_habilitada: e.target.checked })}
            style={{ marginTop: 3, width: 18, height: 18, cursor: 'pointer' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461' }}>
              🏦 Habilitado para transferencia bancaria
            </div>
            <div style={{ fontSize: 11, color: '#64748B', marginTop: 2, lineHeight: 1.4 }}>
              Si está activado, el cliente verá la opción "Pagar por transferencia" en el checkout
              (compras únicas y suscripciones). Vos/Roberto confirman manualmente cada pago desde
              <strong> /admin/cobros-pendientes</strong>.
            </div>
          </div>
        </label>
        {persona.fuera_zona && (
          <div style={{ marginTop: 6, padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 8, fontSize: 11, color: '#78350F' }}>
            🚫 <strong>Fuera de zona</strong> · entrega a cargo de {persona.entrega_responsable || '—'} ·
            <Link to="/admin/clientes-fuera-zona" style={{ marginLeft: 6, color: '#1A3461', fontWeight: 700 }}>gestionar</Link>
          </div>
        )}
      </div>

      {err && <div className="err-box">{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={guardar} disabled={saving || !f.nombre.trim()}>
          {saving ? 'Guardando…' : `✓ Guardar ${esTitular ? 'titular' : 'beneficiario'}`}
        </button>
      </div>
    </>
  )
}

/* Accordion inline con detalle del cliente: composición del kit, últimas entregas, pagos */
function DetalleAccordion({ suscripcion, actividad }) {
  const s = suscripcion
  const [entregasRecientes, setEntregasRecientes] = useState([])
  const [pagosRecientes, setPagosRecientes] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    (async () => {
      const [{ data: entregas }, { data: pagos }] = await Promise.all([
        supabase.from('entregas').select('id, numero_orden, fecha_programada, estado, monto_total, ventana_horaria').eq('cliente_id', s.id).order('fecha_programada', { ascending: false }).limit(5),
        supabase.from('pagos').select('id, monto, estado, metodo, created_at').eq('cliente_id', s.id).order('created_at', { ascending: false }).limit(5),
      ])
      setEntregasRecientes(entregas || [])
      setPagosRecientes(pagos || [])
      setCargando(false)
    })()
  }, [s.id])

  const recibe = s.beneficiario || s.titular
  const t = s.titular

  return (
    <div style={{ padding: '14px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, fontSize: 12 }}>
      {/* Columna 1: Contactos + facturación */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Contactos</div>
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontWeight: 700, color: '#1A3461' }}>{t?.nombre || '—'}</div>
          <div style={{ fontSize: 11, color: '#64748B' }}>Titular (paga)</div>
          <div style={{ fontSize: 11.5, marginTop: 6 }}>
            {t?.phone && <div>📱 {t.phone}</div>}
            {t?.email && <div style={{ color: '#64748B' }}>✉ {t.email}</div>}
            {t?.documento && <div style={{ color: '#64748B' }}>🪪 {t.documento_tipo?.toUpperCase()} {t.documento}</div>}
          </div>
          {s.beneficiario && s.beneficiario.id !== t?.id && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #E2E8F0' }}>
              <div style={{ fontWeight: 700, color: '#7C3AED' }}>🎁 {s.beneficiario.nombre}</div>
              <div style={{ fontSize: 11, color: '#64748B' }}>Recibe el kit</div>
              {s.beneficiario.phone && <div style={{ fontSize: 11.5, marginTop: 4 }}>📱 {s.beneficiario.phone}</div>}
            </div>
          )}
        </div>

        <div style={{ fontSize: 10, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 14, marginBottom: 6 }}>Suscripción</div>
        <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', fontSize: 11.5 }}>
          <div><b>Kit:</b> {s.kit?.nombre || '—'}</div>
          <div><b>Precio susc.:</b> ${Number(s.kit?.precio_susc || 0).toLocaleString('es-UY')}/mes</div>
          <div><b>Modelo cobro:</b> {s.modelo_cobro || 'MP suscripción'}</div>
          {s.dia_cobro_mes && <div><b>Día cobro:</b> {s.dia_cobro_mes}</div>}
          {s.fecha_proximo_cobro && <div><b>Próx. cobro:</b> {s.fecha_proximo_cobro}</div>}
          <div><b>Inicio:</b> {s.fecha_inicio || '—'}</div>
          {s.notas && <div style={{ marginTop: 6, padding: 6, background: '#FEF3C7', borderRadius: 4, fontSize: 10.5 }}>📝 {s.notas}</div>}
        </div>
      </div>

      {/* Columna 2: Entregas recientes */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em' }}>Últimas entregas</div>
          {actividad.entregasHechas > 0 && (
            <div style={{ fontSize: 10, color: '#94A3B8' }}>{actividad.entregasHechas} entregadas totales</div>
          )}
        </div>
        {cargando ? (
          <div style={{ fontSize: 11, color: '#94A3B8' }}>Cargando…</div>
        ) : entregasRecientes.length === 0 ? (
          <div style={{ background: '#fff', border: '1px dashed #E2E8F0', borderRadius: 8, padding: 12, fontSize: 11.5, color: '#94A3B8', textAlign: 'center' }}>Sin entregas todavía</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {entregasRecientes.map(e => (
              <div key={e.id} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5 }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: '#0369A1' }}>{e.numero_orden}</div>
                  <div style={{ fontSize: 10, color: '#64748B' }}>{e.fecha_programada} · {e.ventana_horaria}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1A3461' }}>${Math.round(e.monto_total).toLocaleString('es-UY')}</span>
                  <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 999, background: (ESTADO_ENTREGA_COL[e.estado] || ESTADO_ENTREGA_COL.programada).bg, color: (ESTADO_ENTREGA_COL[e.estado] || ESTADO_ENTREGA_COL.programada).text, fontWeight: 700, textTransform: 'uppercase' }}>{e.estado}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {actividad.oneShots > 0 && (
          <div style={{ marginTop: 12, padding: '8px 10px', background: '#FEF3C7', borderRadius: 6, fontSize: 11 }}>
            🛍 <b>{actividad.oneShots}</b> compra{actividad.oneShots !== 1 ? 's' : ''} one-shot histórica · <b>${Math.round(actividad.montoOneShot).toLocaleString('es-UY')}</b> total
          </div>
        )}
      </div>

      {/* Columna 3: Pagos + acciones rápidas */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Últimos pagos</div>
        {cargando ? (
          <div style={{ fontSize: 11, color: '#94A3B8' }}>Cargando…</div>
        ) : pagosRecientes.length === 0 ? (
          <div style={{ background: '#fff', border: '1px dashed #E2E8F0', borderRadius: 8, padding: 12, fontSize: 11.5, color: '#94A3B8', textAlign: 'center' }}>Sin pagos registrados</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pagosRecientes.map(p => (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5 }}>
                <div>
                  <div style={{ fontSize: 10.5, color: '#64748B' }}>{new Date(p.created_at).toLocaleDateString('es-UY')}</div>
                  <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.metodo || '—'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1A3461' }}>${Math.round(p.monto).toLocaleString('es-UY')}</span>
                  <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 999, background: p.estado === 'aprobado' ? '#D1FAE5' : '#FEF3C7', color: p.estado === 'aprobado' ? '#065F46' : '#78350F', fontWeight: 700, textTransform: 'uppercase' }}>{p.estado}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const ESTADO_ENTREGA_COL = {
  programada: { bg: '#E0F2FE', text: '#0C4A6E' },
  en_ruta: { bg: '#FEF3C7', text: '#92400E' },
  entregada: { bg: '#D1FAE5', text: '#065F46' },
  no_entregada: { bg: '#FEE2E2', text: '#991B1B' },
  cancelada: { bg: '#F1F5F9', text: '#475569' },
}

const modalBg = {
  position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, overflowY: 'auto',
}
const modalCard = {
  background: '#fff', borderRadius: 14, padding: 24,
  maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto',
}

// Selector de tipo de alta (rebuild 2026-09-02): suscripción / compra única (venta manual) / solo contacto.
function ElegirTipoCliente({ onClose, onSuscripcion, onContacto }) {
  const card = { border: '1px solid #E2E8F0', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', textDecoration: 'none', color: 'inherit', background: '#fff' }
  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 460 }}>
        <h2 style={{ margin: '0 0 4px' }}>Nuevo cliente</h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 18 }}>¿Qué tipo de alta?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={card} onClick={onSuscripcion} onMouseDown={e => e.preventDefault()}>
            <span style={{ fontSize: 22 }}>🔁</span>
            <div><div style={{ fontWeight: 700, color: '#1A3461' }}>Suscripción mensual</div><div style={{ fontSize: 12, color: '#64748B' }}>Cliente recurrente: kit, día de entrega y cobro mensual.</div></div>
          </div>
          <Link to="/admin/ventas-manuales" style={card}>
            <span style={{ fontSize: 22 }}>🛒</span>
            <div><div style={{ fontWeight: 700, color: '#1A3461' }}>Compra única (one-shot)</div><div style={{ fontSize: 12, color: '#64748B' }}>Venta puntual sin suscripción. Se carga en Venta manual.</div></div>
          </Link>
          <div style={card} onClick={onContacto}>
            <span style={{ fontSize: 22 }}>👤</span>
            <div><div style={{ fontWeight: 700, color: '#1A3461' }}>Solo contacto</div><div style={{ fontSize: 12, color: '#64748B' }}>Guardar la persona para venderle después.</div></div>
          </div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}><button className="btn ghost" onClick={onClose}>Cancelar</button></div>
      </div>
    </div>
  )
}

// Alta mínima de contacto (persona) sin suscripción ni pedido.
function ModalContacto({ onClose, onCreated }) {
  const [nombre, setNombre] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [documento, setDocumento] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  async function guardar() {
    if (!nombre.trim()) { setErr('Poné al menos el nombre.'); return }
    setSaving(true); setErr(null)
    try {
      await crearPersona({ nombre, phone: phone || null, email: email || null, documento: documento || null, documentoTipo: documento ? 'ci' : null })
      onCreated()
    } catch (e) { setErr(e.message || 'No se pudo crear el contacto') } finally { setSaving(false) }
  }
  const inp = { width: '100%', padding: '8px 11px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' }
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', marginBottom: 4 }
  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h2 style={{ margin: '0 0 4px' }}>👤 Nuevo contacto</h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 16 }}>Guardá la persona para venderle después (sin suscripción ni pedido).</p>
        <label style={lbl}>Nombre y apellido *</label>
        <input style={inp} value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ana Somma" />
        <label style={lbl}>Teléfono</label>
        <input style={inp} value={phone} onChange={e => setPhone(e.target.value)} placeholder="099 123 456" />
        <label style={lbl}>Email</label>
        <input style={inp} value={email} onChange={e => setEmail(e.target.value)} placeholder="ana@mail.com" />
        <label style={lbl}>Documento (CI)</label>
        <input style={inp} value={documento} onChange={e => setDocumento(e.target.value)} placeholder="4.123.456-7" />
        {err && <div style={{ color: '#B91C1C', fontSize: 12, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving || !nombre.trim()}>{saving ? 'Guardando…' : 'Crear contacto'}</button>
        </div>
      </div>
    </div>
  )
}
